import React, { FC, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  Col,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from 'reactstrap';
import { ChildRegistration } from '../models';
import { useTranslation } from '../translations';
import { useConvention } from '../utils';
import { ElementListItem } from './ElementList';
import { JSONForm } from './json-form/JSONForm';
import { ElementList, HotkeyButton, MaterialIcon, TermsLoader } from '.';

const convertToElements = (children: ChildRegistration[]): ElementListItem[] => {
  return children.map((child) => ({
    id: child.id,
    name: `${child.badgeName}${child.species ? ` (${child.species})` : ''}`,
  }));
};

interface ChildRegistrationProps {
  readonly hideWarning?: boolean;
  readonly compact?: boolean;
  readonly useModal?: boolean;
  readonly items: ChildRegistration[];
  readonly registrationId: number;
  readonly onUpdate: () => void;
}

export const ChildRegistrations: FC<ChildRegistrationProps> = ({
  items,
  compact,
  useModal,
  hideWarning,
  registrationId,
  onUpdate,
}) => {
  const { ts } = useTranslation();
  const onDelete = useCallback(
    async (id: number) => {
      await api.deleteChildRegistration(id);
      toast.success(ts('child_registrations.child_registration_has_been_removed'));
      onUpdate();
    },
    [onUpdate, ts],
  );

  const onAdd = useCallback(() => {
    toast.success(ts('child_registrations.new_child_registration_has_been'));
    onUpdate();
  }, [onUpdate, ts]);

  return (
    <Row className="justify-content-center" id="childRegistration">
      {!hideWarning && (
        <Col className="mb-10" xs={12}>
          <Card color="info" outline>
            <CardBody>
              <CardTitle>{ts('terms.child_registrations.child_badge_terms')}</CardTitle>
              <Row>
                <Col className="text-center p-0" lg={2} xs={12}>
                  <MaterialIcon large name="info" type="info" />
                </Col>
                <Col className="p-0" lg={10} xs={12}>
                  <TermsLoader name="childBadgeTerms" />
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      )}
      <Col className="mb-10" lg={compact ? 12 : 6} xs={12}>
        <ElementList elements={convertToElements(items)} onDelete={onDelete} />
      </Col>
      <Col className="mb-10" lg={compact ? 12 : 6} xs={12}>
        {useModal ? (
          <AddChildModal onAdd={onAdd} registrationId={registrationId} />
        ) : (
          <AddChildInline onAdd={onAdd} registrationId={registrationId} />
        )}
      </Col>
    </Row>
  );
};

interface AddChildProps {
  readonly registrationId: number;
  readonly onAdd: () => void;
}

const AddChildInline: FC<AddChildProps> = ({ registrationId, onAdd }) => {
  const convention = useConvention();

  const { ts } = useTranslation();
  return (
    <JSONForm
      method="post"
      onSuccess={onAdd}
      path={`/api/registrations/${registrationId}/children`}
    >
      <Card>
        <CardBody>
          <CardTitle>{ts('child_registrations.add_new_child')}</CardTitle>
          <Row>
            <Col md={6} xs={12}>
              <FormGroup>
                <Label for="badgeName">{ts('common.badge_name')}</Label>
                <Input id="badgeName" name="badgeName" />
              </FormGroup>
            </Col>
            {convention.childSubLabel ? (
              <Col md={6} xs={12}>
                <FormGroup>
                  <Label for="species">
                    {convention.childSubLabel} <small>({ts('common.optional')})</small>
                  </Label>
                  <Input id="species" name="species" />
                </FormGroup>
              </Col>
            ) : (
              <Input id="species" name="species" type="hidden" value="" />
            )}
            <Col className="mt-10" lg={12} xs={12}>
              <Button block color="primary" id="submitForm" type="submit">
                {ts('child_registrations.add_child')}
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>
    </JSONForm>
  );
};

const AddChildModal: FC<AddChildProps> = ({ registrationId, onAdd: baseOnAdd }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { ts } = useTranslation();

  const onAdd = useCallback(() => {
    setIsOpen(false);
    baseOnAdd();
  }, [baseOnAdd]);

  return (
    <>
      <HotkeyButton
        actionText="child_registrations.add_new_child_badge"
        block
        color="secondary"
        hotkey="c"
        onClick={() => {
          setIsOpen(true);
        }}
        outline
      >
        {ts('child_registrations.add_new_child_badge')}
      </HotkeyButton>
      <Modal
        className="modal-large child-registration-modal hide-print"
        isOpen={isOpen}
        toggle={() => {
          setIsOpen(false);
        }}
      >
        <ModalHeader
          toggle={() => {
            setIsOpen(false);
          }}
        >
          {ts('child_registrations.add_child_badge')}
        </ModalHeader>
        <ModalBody>
          <AddChildInline onAdd={onAdd} registrationId={registrationId} />
        </ModalBody>
      </Modal>
    </>
  );
};
